export default class StreamingService {
  constructor(wsUrl, station, maxReconnects = 10) {
    
    if (/^wss?:\/\/.*/.test(wsUrl)) {
      this.wsUrl = wsUrl;  
    } else {
      const wsScheme = window.location.protocol === "https:" ? "wss" : "ws";
      this.wsUrl = `${wsScheme}://${window.location.host}${wsUrl}`;
    }

    this.station = station;
    this.events = {};
    this.socket = null;
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this.maxReconnects = maxReconnects;
    this.isPaused = false;

    this.connect();
  }

  connect() {
    if (this.isPaused || !this.wsUrl) return;

    if (this.socket && 
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    if (this.socket) {
      this.cleanupSocket();
    }

    if (this.reconnectAttempts >= this.maxReconnects) {
      console.warn("Max reconnect attempts reached.");
      return;
    }

    this.socket = new WebSocket(this.wsUrl);

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;

      this.socket.send(
        JSON.stringify({
          subs: {
            ['station:' + this.station]: { recover: true },
          },
        })
      );
    };

    this.socket.onerror = () => this.reconnect();
    this.socket.onclose = () => this.reconnect();

    this.socket.onmessage = (event) => {
      let data;

      try {
        data = JSON.parse(event.data);
      } catch (e) { return; }

      if ('connect' in data) {
        const connectData = data.connect;
        if ('data' in connectData) {
          // Legacy SSE data
          connectData.data.forEach(
            (initialRow) => this.handleSseData(initialRow)
          );
        } else {
          let currentTime = +new Date() / 1000;
          // New Centrifugo time format
          if ('time' in connectData) {
            currentTime = Math.floor(connectData.time / 1000);
          }

          for (const subName in connectData.subs) {
            const sub = connectData.subs[subName];
            if ('publications' in sub && sub.publications.length > 0) {
              sub.publications.forEach((initialRow) => this.handleSseData(initialRow, false, currentTime));
            }
          }
        }
      } else if ('pub' in data) {
        this.handleSseData(data.pub);
      }
    };
  }

  cleanupSocket() {
    if (!this.socket) return;
    try {
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.close();
    } catch (e) { }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.socket = null;
  }

  pause() {
    this.isPaused = true;
    //clearTimeout(this.reconnectTimer);
    this.cleanupSocket();
  }

  resume() {
    if (!this.isPaused) return;
    this.isPaused = false;
    this.connect();
  }

  reconnect() {
    if (this.isPaused) return;
    clearTimeout(this.reconnectTimer);

    if (this.reconnectAttempts >= this.maxReconnects) return;
    this.reconnectAttempts++;

    const delay = Math.min(1000 * 2 ** (this.reconnectAttempts - 1), 60000);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  destroy() {
    this.pause();
    this.events = {};
  }

  handleSseData(ssePayload, useTime = true, currentTime = undefined) {
      const jsonData = ssePayload.data;

      if (useTime && 'current_time' in jsonData) {
          currentTime = jsonData.current_time;
      }

      let nowplaying = jsonData.np;

      if (nowplaying?.now_playing?.song) {
          const song = nowplaying.now_playing.song;

          const track = {
            id: song.id,
            title: song.text,
            duration: nowplaying.now_playing.duration,
            elapsed: Math.floor(Date.now() / 1000) - nowplaying.now_playing.played_at,
            startedAt: nowplaying.now_playing.played_at,
            currentTime: currentTime,
            isLive: nowplaying.now_playing?.live?.is_live
          };

          // note: we can emit this only when track changes ('song_changed' in np.triggers (?))
          // though we may de-sync a bit
          this.emit("trackUpdate", track);
      }
  }

  // --- Event system ---
  on(event, handler) {
    (this.events[event] ??= new Set()).add(handler);
  }

  off(event, handler) {
    this.events[event]?.delete(handler);
  }

  emit(event, data) {
    if (!this.events[event]) return;
    for (const h of this.events[event]) h(data);
  }
}